// src/pages/HelpSupport/HelpSupport.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
 
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
};
 
const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px',
    border: `1.5px solid ${C.outlineVariant}`,
    borderRadius: '10px', fontSize: '0.95rem',
    boxSizing: 'border-box',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    background: C.surface, color: C.onSurface,
    outline: 'none', transition: 'border-color 0.15s',
};
 
const labelStyle: React.CSSProperties = {
    display: 'block', marginBottom: '6px',
    fontSize: '0.8rem', fontWeight: '700',
    color: C.onSurfaceVariant,
    fontFamily: "'Epilogue', sans-serif",
};
 
const faqs = [
    {
        question: "How do you ensure child safety on the platform?",
        answer: "Safety is our top priority. We use AI-powered content moderation that flags inappropriate content, usernames cannot contain real names or personal information, all shared stories are moderated before going live, we provide an easy reporting system for users, parent/teacher oversight features, COPPA-compliant data practices, and no direct messaging between students without teacher/parent approval."
    },
    {
        question: "Can parents monitor their child's activity?",
        answer: "Yes! Parent accounts can view their child's stories, see their activity feed, monitor who they're sharing with, and receive notifications about their progress. Parents can also set permissions for what their child can access, including whether they can publish stories publicly or only share with approved users."
    },
    {
        question: "What age is this platform appropriate for?",
        answer: "Scriblish is designed for children ages 8–12. We offer age-appropriate features and challenges, with simplified interfaces and guided writing prompts for younger writers, and more creative freedom for older ones."
    },
    {
        question: "Is student data protected?",
        answer: "Absolutely. We are COPPA and FERPA compliant. We never sell student data, share personal information with third parties, or use student content for advertising. All data is encrypted, and we only collect the minimum information necessary. Parents can request data deletion at any time."
    },
    {
        question: "Can teachers use this in the classroom?",
        answer: "Yes! We offer educator features including class management tools, assignment creation, student progress tracking, and curriculum-aligned writing prompts. Teachers can create a class code for students to join and have full oversight of all student activity."
    },
    {
        question: "What if my child forgets their password?",
        answer: "Use the 'Forgot Password' link on the login page. As the parent account holder, you'll receive a password reset email. You can also reset their password directly from your parent dashboard under 'Manage Child Accounts'."
    },
    {
        question: "Are there any costs or in-app purchases?",
        answer: "The basic version is completely free with no ads. There are no surprise charges or in-app purchases accessible to children."
    },
    {
        question: "How do I delete my child's account?",
        answer: "You can delete your child's account at any time from your parent dashboard under Settings > Manage Accounts > Delete Account. You'll be able to download all their stories before deletion. All personal data is permanently removed within 30 days."
    },
    {
        question: "How do children write their first story?",
        answer: "Click the 'Write' button in the menu, then start a new story. Give it a title and start typing — the story saves automatically. There's no pressure to make it perfect, just have fun writing!"
    },
    {
        question: "Can children share their stories with friends?",
        answer: "Yes! When a story is finished, they can publish it to the Stories page where other kids can read it. Parents control sharing permissions in their dashboard."
    },
];
 
const HelpSupport: React.FC = () => {
    const navigate = useNavigate();
    const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
    const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
 
    const handleContactSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormLoading(true);
        try {
            const db = getFirestore();
            await addDoc(collection(db, 'contact_submissions'), {
                ...contactForm,
                timestamp: new Date(),
                status: 'unread',
            });
            setFormSubmitted(true);
            setTimeout(() => {
                setFormSubmitted(false);
                setContactForm({ name: '', email: '', subject: '', message: '' });
            }, 3000);
        } catch (error) {
            console.error('Error saving message:', error);
            alert('Failed to send message. Please try again.');
        } finally {
            setFormLoading(false);
        }
    };
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif", padding: '48px 24px 80px' }}>
            <div style={{ maxWidth: '760px', margin: '0 auto' }}>
 
                {/* Header */}
                <div style={{ marginBottom: '40px' }}>
                    <div style={{ display: 'inline-block', background: C.primaryContainer, color: C.primary, padding: '4px 14px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: "'Epilogue', sans-serif", marginBottom: '16px' }}>
                        Support
                    </div>
                    <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '2.4rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                        Help & Support
                    </h1>
                    <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: 0, lineHeight: '1.6' }}>
                        Find answers to common questions, or send us a message and we'll get back to you within 24 hours.
                    </p>
                </div>
 
                {/* Quick info cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '32px' }}>
                    {[
                        { icon: '🛡️', label: 'Safety Center', href: '/safety',               bg: C.primaryContainer, color: C.primary },
                        { icon: '📜', label: 'Community Guidelines', href: '/community-guidelines', bg: C.secondaryContainer, color: C.secondary },
                        { icon: '🔒', label: 'Privacy Policy', href: '/privacy-policy',       bg: C.surfaceHigh, color: C.onBackground },
                    ].map(card => (
                        <a key={card.label} href={card.href} style={{
                            background: card.bg, borderRadius: '14px', padding: '18px 16px',
                            textDecoration: 'none', display: 'flex', flexDirection: 'column',
                            alignItems: 'center', gap: '8px', textAlign: 'center',
                            transition: 'transform 0.15s, box-shadow 0.15s',
                            border: `1px solid ${card.color}20`,
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                        >
                            <span style={{ fontSize: '1.6rem' }}>{card.icon}</span>
                            <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.82rem', color: card.color }}>{card.label}</span>
                        </a>
                    ))}
                </div>
 
                {/* Main card */}
                <div style={{ background: '#fff', borderRadius: '20px', padding: '48px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: `1px solid ${C.surfaceHigh}` }}>
 
                    {/* Safety overview */}
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.35rem', fontWeight: '900', color: C.onBackground, margin: '0 0 12px 0', paddingBottom: '8px', borderBottom: `2px solid ${C.surfaceHigh}` }}>
                        Safety & Privacy
                    </h2>
                    <p style={{ fontSize: '0.95rem', lineHeight: '1.8', color: C.onSurface, margin: '0 0 14px 0' }}>
                        Child safety is our top priority. Our platform includes:
                    </p>
                    <ul style={{ margin: '0 0 32px 0', paddingLeft: '20px' }}>
                        {[
                            'AI-powered content moderation for all shared content',
                            'Username protection that blocks personal information',
                            'Parent dashboard for monitoring activity',
                            'Easy reporting system for inappropriate content',
                            'COPPA and FERPA compliance',
                            'Encrypted data storage and transmission',
                        ].map(item => (
                            <li key={item} style={{ fontSize: '0.95rem', lineHeight: '1.8', color: C.onSurface, marginBottom: '6px' }}>{item}</li>
                        ))}
                    </ul>
 
                    {/* Getting started */}
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.35rem', fontWeight: '900', color: C.onBackground, margin: '0 0 12px 0', paddingBottom: '8px', borderBottom: `2px solid ${C.surfaceHigh}` }}>
                        Getting Started
                    </h2>
                    <ol style={{ margin: '0 0 32px 0', paddingLeft: '20px' }}>
                        {[
                            'Create an account with parental consent (for users under 13)',
                            'Choose a username that doesn\'t include personal information',
                            'Explore the dashboard to see available features',
                            'Click "Write" to start your first story',
                            'Use the parent dashboard to monitor activity and adjust settings',
                        ].map(item => (
                            <li key={item} style={{ fontSize: '0.95rem', lineHeight: '1.8', color: C.onSurface, marginBottom: '6px' }}>{item}</li>
                        ))}
                    </ol>
 
                    {/* FAQ accordion */}
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.35rem', fontWeight: '900', color: C.onBackground, margin: '0 0 20px 0', paddingBottom: '8px', borderBottom: `2px solid ${C.surfaceHigh}` }}>
                        Frequently Asked Questions
                    </h2>
 
                    <div style={{ marginBottom: '40px' }}>
                        {faqs.map((faq, i) => (
                            <div key={i} style={{
                                borderBottom: `1px solid ${C.surfaceHigh}`,
                                overflow: 'hidden',
                            }}>
                                <button
                                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                                    style={{
                                        width: '100%', textAlign: 'left',
                                        background: 'none', border: 'none',
                                        padding: '16px 0', cursor: 'pointer',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        gap: '16px',
                                    }}
                                >
                                    <span style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '0.95rem', fontWeight: '800', color: C.onBackground, lineHeight: '1.4' }}>
                                        {faq.question}
                                    </span>
                                    <div style={{
                                        width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                                        background: expandedFaq === i ? C.primary : C.surfaceLow,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: expandedFaq === i ? '#fff' : C.onSurfaceVariant,
                                        fontSize: '1.1rem', fontWeight: '700',
                                        transition: 'all 0.15s',
                                    }}>
                                        {expandedFaq === i ? '−' : '+'}
                                    </div>
                                </button>
                                {expandedFaq === i && (
                                    <div style={{ paddingBottom: '16px' }}>
                                        <p style={{ fontSize: '0.9rem', lineHeight: '1.75', color: C.onSurfaceVariant, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                            {faq.answer}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
 
                    {/* Contact form */}
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.35rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0', paddingBottom: '8px', borderBottom: `2px solid ${C.surfaceHigh}` }}>
                        Contact Support
                    </h2>
                    <p style={{ fontSize: '0.95rem', lineHeight: '1.8', color: C.onSurfaceVariant, margin: '0 0 24px 0' }}>
                        Can't find what you're looking for? Send us a message and we'll get back to you within 24 hours.
                    </p>
 
                    {formSubmitted ? (
                        <div style={{ background: C.secondaryContainer, border: `1.5px solid ${C.secondary}30`, borderRadius: '14px', padding: '32px', textAlign: 'center', marginBottom: '32px' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✅</div>
                            <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.2rem', fontWeight: '900', color: C.secondary, margin: '0 0 8px 0' }}>Message Sent!</h3>
                            <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: 0 }}>We'll respond to your email within 24 hours.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleContactSubmit} style={{ marginBottom: '40px' }}>
                            {/* Name + Email row */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={labelStyle}>Your Name</label>
                                    <input
                                        type="text" value={contactForm.name} required
                                        placeholder="Enter your name"
                                        onChange={e => setContactForm({ ...contactForm, name: e.target.value })}
                                        style={inputStyle}
                                        onFocus={e  => { e.target.style.borderColor = C.primary; }}
                                        onBlur={e   => { e.target.style.borderColor = C.outlineVariant; }}
                                    />
                                </div>
                                <div>
                                    <label style={labelStyle}>Email Address</label>
                                    <input
                                        type="email" value={contactForm.email} required
                                        placeholder="your@email.com"
                                        onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                                        style={inputStyle}
                                        onFocus={e  => { e.target.style.borderColor = C.primary; }}
                                        onBlur={e   => { e.target.style.borderColor = C.outlineVariant; }}
                                    />
                                </div>
                            </div>
 
                            {/* Subject */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={labelStyle}>Subject</label>
                                <select
                                    value={contactForm.subject} required
                                    onChange={e => setContactForm({ ...contactForm, subject: e.target.value })}
                                    style={{ ...inputStyle, background: '#fff' }}
                                    onFocus={e  => { (e.target as HTMLSelectElement).style.borderColor = C.primary; }}
                                    onBlur={e   => { (e.target as HTMLSelectElement).style.borderColor = C.outlineVariant; }}
                                >
                                    <option value="">Select a topic…</option>
                                    <option value="technical">Technical Issue</option>
                                    <option value="account">Account Help</option>
                                    <option value="safety">Safety Concern</option>
                                    <option value="feature">Feature Request</option>
                                    <option value="billing">Billing Question</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
 
                            {/* Message */}
                            <div style={{ marginBottom: '24px' }}>
                                <label style={labelStyle}>Message</label>
                                <textarea
                                    value={contactForm.message} required
                                    placeholder="Tell us how we can help…"
                                    rows={5}
                                    onChange={e => setContactForm({ ...contactForm, message: e.target.value })}
                                    style={{
                                        ...inputStyle,
                                        resize: 'vertical',
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    }}
                                    onFocus={e  => { (e.target as HTMLTextAreaElement).style.borderColor = C.primary; }}
                                    onBlur={e   => { (e.target as HTMLTextAreaElement).style.borderColor = C.outlineVariant; }}
                                />
                            </div>
 
                            <button
                                type="submit"
                                disabled={formLoading}
                                style={{
                                    background: formLoading
                                        ? C.outlineVariant
                                        : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                                    color: '#fff', border: 'none',
                                    padding: '13px 36px', borderRadius: '9999px',
                                    fontSize: '0.95rem', fontWeight: '700',
                                    cursor: formLoading ? 'not-allowed' : 'pointer',
                                    fontFamily: "'Epilogue', sans-serif",
                                    boxShadow: formLoading ? 'none' : `0 4px 14px ${C.primary}40`,
                                    transition: 'opacity 0.15s, transform 0.15s',
                                }}
                                onMouseEnter={e => { if (!formLoading) { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                                {formLoading ? 'Sending…' : 'Send Message'}
                            </button>
                        </form>
                    )}
 
                    {/* Footer links */}
                    <div style={{ paddingTop: '28px', borderTop: `1px solid ${C.surfaceHigh}`, display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {[
                            { label: 'Safety Center',        href: '/safety' },
                            { label: 'Community Guidelines', href: '/community-guidelines' },
                            { label: 'Privacy Policy',       href: '/privacy-policy' },
                            { label: 'Terms of Service',     href: '/terms' },
                            { label: 'Contact Us',           href: '/contact' },
                        ].map(({ label, href }) => (
                            <a key={label} href={href} style={{ color: C.primary, textDecoration: 'none', fontSize: '0.85rem', fontWeight: '600', fontFamily: "'Epilogue', sans-serif", transition: 'opacity 0.15s' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                            >
                                {label}
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
 
export default HelpSupport;